import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { apiService } from '../services/api';
import ProjectCard from '../components/projects/ProjectCard';
import ProjectFilter from '../components/projects/ProjectFilter';
import LoadingSpinner from '../components/common/LoadingSpinner';

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const categories = ['all', 'web', 'blockchain', 'embedded', 'ai', 'mobile', 'backend'];

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await apiService.getProjects();
        setProjects(response.data || []);
        setFiltered(response.data || []);
      } catch (error) {
        console.error('Error fetching projects:', error);
        // Fallback data
        const fallbackData = [
          {
            id: 1,
            title: 'HealthGuardX',
            slug: 'healthguardx',
            description: 'Decentralized Healthcare Identity Platform using blockchain and IPFS',
            tech_stack: ['Solidity', 'React', 'TypeScript', 'Solana', 'IPFS'],
            github_url: 'https://github.com/developer13-art/HealthGuard',
            live_url: 'https://healthguard-1-pba4.onrender.com',
            category: 'blockchain',
            featured: true,
            hero_image: '/assets/images/healthguardx.jpg'
          },
          {
            id: 2,
            title: 'FYP Assist Hub',
            slug: 'fyp-assist-hub',
            description: 'Digital marketplace connecting university students with supervisors and developers',
            tech_stack: ['React', 'Node.js', 'PostgreSQL', 'Tailwind CSS'],
            category: 'web',
            featured: true
          },
          {
            id: 3,
            title: 'Emergency Alert System',
            slug: 'emergency-alert-system',
            description: 'Web platform integrating software with embedded hardware for emergency response',
            tech_stack: ['React', 'Laravel', 'MySQL', 'IoT', 'GPS'],
            category: 'embedded',
            featured: true
          }
        ];
        setProjects(fallbackData);
        setFiltered(fallbackData);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  const handleFilter = (category) => {
    setActiveFilter(category);
    if (category === 'all') {
      setFiltered(projects);
    } else {
      setFiltered(projects.filter(project => project.category === category));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <span className="inline-block px-4 py-2 rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-400 text-sm font-medium mb-4">
            Portfolio
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold">
            My <span className="gradient-text">Projects</span>
          </h1>
          <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
            A showcase of my work across various domains and technologies
          </p>
        </motion.div>

        {/* Filter */}
        <ProjectFilter 
          categories={categories}
          activeFilter={activeFilter}
          onFilter={handleFilter}
        />

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((project, index) => (
            <ProjectCard 
              key={project.id} 
              project={project}
              index={index}
            />
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-400">No projects found for this category.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Projects;